import { useState, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';

type Suggestion = string | { label: string; value: string };

type FetchSuggestions = (input: string) => Promise<Suggestion[]>;

const DEBOUNCE_DELAY = 300;

/**
 * A hook to fetch text input suggestions with debouncing.
 *
 * @param input The text input from the user.
 * @param fetchSuggestions An async function to fetch suggestions based on the input.
 * @param threshold The minimum input length to trigger fetching suggestions. Defaults to 2.
 * @returns An array of suggestions.
 */
const useTextInputSuggestions = ({
  input,
  fetchSuggestions,
  threshold = 2,
}: {
  input?: string;
  fetchSuggestions: FetchSuggestions;
  threshold?: number;
}): Suggestion[] => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const fetchSuggestionsRef = useRef(fetchSuggestions);

  useEffect(() => {
    fetchSuggestionsRef.current = fetchSuggestions;
  }, [fetchSuggestions]);

  const debouncedFetch = useMemo(() => {
    const fetcher = (inputValue: string) => {
      fetchSuggestionsRef
        .current(inputValue)
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    };
    return debounce(fetcher, DEBOUNCE_DELAY);
  }, []);

  useEffect(() => {
    const trimmedInput = input?.trim() || '';

    if (trimmedInput.length < threshold) {
      setSuggestions([]);
      return;
    }

    debouncedFetch(trimmedInput);
  }, [input, threshold, debouncedFetch]);

  return suggestions;
};

export default useTextInputSuggestions;
